import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SettingsContext = createContext(null);

// Hex → "r, g, b" for rgba() usage
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return isNaN(r) ? null : `${r}, ${g}, ${b}`;
}

// Derive a readable "on-color" (black or white) based on luminance
function onColor(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.35 ? '#001820' : '#e0e3e4';
}

// Lighten a hex color by mixing with white
function lighten(hex, amount = 0.7) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0');
  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

/**
 * Apply full branding token set derived from primary + secondary colors.
 *
 * In dark mode: team colors become --primary / --secondary (full override).
 * In light mode: theme tokens stay intact; team colors go to --brand-primary /
 * --brand-secondary only, so light surfaces/text are never disturbed.
 */
export function applyBranding(primary, secondary) {
  const root = document.querySelector(':root');
  if (!root) return;

  const isLight = document.documentElement.getAttribute('data-theme') === 'light';

  // Always store raw brand colors so components can reference them
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-secondary', secondary || '#feb700');
  const rgb = hexToRgb(primary);
  if (rgb) root.style.setProperty('--brand-primary-rgb', rgb);

  if (isLight) {
    // Light mode: clear any dark-mode inline overrides, let CSS variables rule
    root.style.removeProperty('--primary');
    root.style.removeProperty('--primary-dim');
    root.style.removeProperty('--primary-rgb');
    root.style.removeProperty('--on-primary');
    root.style.removeProperty('--primary-container');
    root.style.removeProperty('--surface-tint');
    root.style.removeProperty('--secondary');
    root.style.removeProperty('--secondary-container');
    root.style.removeProperty('--secondary-rgb');
  } else {
    // Dark mode: team colors override the design-system primary/secondary
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--primary-dim', primary);
    root.style.setProperty('--surface-tint', primary);
    if (rgb) root.style.setProperty('--primary-rgb', rgb);
    root.style.setProperty('--on-primary', onColor(primary));
    root.style.setProperty('--primary-container', lighten(primary, 0.85));

    if (secondary) {
      root.style.setProperty('--secondary', secondary);
      root.style.setProperty('--secondary-container', secondary);
      const secRgb = hexToRgb(secondary);
      if (secRgb) root.style.setProperty('--secondary-rgb', secRgb);
    }
  }

  // Always set brand-secondary-rgb regardless of theme
  if (secondary) {
    const secRgb = hexToRgb(secondary);
    if (secRgb) root.style.setProperty('--brand-secondary-rgb', secRgb);
  }
}

export function SettingsProvider({ token, children }) {
  const [teamName,        setTeamName]        = useState('RENNTEAM');
  const [colorPrimary,    setColorPrimary]    = useState('#00daf3');
  const [colorSecondary,  setColorSecondary]  = useState('#feb700');
  const [logoBase64,      setLogoBase64]      = useState('');
  const [loaded,          setLoaded]          = useState(false);

  const loadSettings = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get('/api/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const name = data.team_name      || 'RENNTEAM';
      const p    = data.color_primary  || '#00daf3';
      const s    = data.color_secondary || '#feb700';
      const logo = data.logo_base64    || '';
      setTeamName(name);
      setColorPrimary(p);
      setColorSecondary(s);
      setLogoBase64(logo);
      applyBranding(p, s);
      localStorage.setItem('teamSettings', JSON.stringify({ teamName: name, colorPrimary: p, colorSecondary: s, logoBase64: logo }));
    } catch {
      const cached = localStorage.getItem('teamSettings');
      if (cached) {
        try {
          const s = JSON.parse(cached);
          if (s.teamName)       setTeamName(s.teamName);
          if (s.colorPrimary)   setColorPrimary(s.colorPrimary);
          if (s.colorSecondary) setColorSecondary(s.colorSecondary);
          if (s.logoBase64)     setLogoBase64(s.logoBase64);
          if (s.colorPrimary)   applyBranding(s.colorPrimary, s.colorSecondary || '#feb700');
        } catch {}
      }
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    // Instant render from cache before API responds
    const cached = localStorage.getItem('teamSettings');
    if (cached) {
      try {
        const s = JSON.parse(cached);
        if (s.teamName)       setTeamName(s.teamName);
        if (s.colorPrimary)   setColorPrimary(s.colorPrimary);
        if (s.colorSecondary) setColorSecondary(s.colorSecondary);
        if (s.logoBase64)     setLogoBase64(s.logoBase64);
        if (s.colorPrimary)   applyBranding(s.colorPrimary, s.colorSecondary || '#feb700');
      } catch {}
    }
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async ({ teamName: name, colorPrimary: p, colorSecondary: s, logoBase64: logo }) => {
    await axios.put('/api/settings', {
      team_name:       name,
      color_primary:   p,
      color_secondary: s,
      logo_base64:     logo,
    }, { headers: { Authorization: `Bearer ${token}` } });
    setTeamName(name);
    setColorPrimary(p);
    setColorSecondary(s);
    setLogoBase64(logo);
    applyBranding(p, s);
    localStorage.setItem('teamSettings', JSON.stringify({ teamName: name, colorPrimary: p, colorSecondary: s, logoBase64: logo }));
  };

  return (
    <SettingsContext.Provider value={{
      teamName, colorPrimary, colorSecondary, logoBase64, loaded,
      saveSettings, applyBranding,
      setColorPrimary, setColorSecondary, setLogoBase64,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}
